import React, { useState } from 'react';
import { ArrowRight, Video, Palette, Globe, Megaphone, Users, Zap, BarChart3, Rocket, MessageCircle, Camera, Code, Smartphone } from 'lucide-react';
import LoginModal from '../../Pages/LoginPage';

const CommunityTool = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const services = [
    {
      icon: <Megaphone className="w-6 h-6" />,
      title: "Social Media Management",
      description: "Complete content creation, scheduling, and community engagement"
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Video Production",
      description: "Professional video content for ads, social media, and branding"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Web Development",
      description: "Custom websites and web applications that drive results"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Brand Design",
      description: "Complete branding packages including logos and visual identity"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Digital Marketing",
      description: "Data-driven campaigns across all digital channels"
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Content Strategy",
      description: "Strategic content planning and execution for maximum impact"
    }
  ];

  const results = [
    { value: "3.2x", label: "Higher Engagement" },
    { value: "2.8x", label: "More Leads" },
    { value: "67%", label: "Faster Growth" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#FD5F20]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FD5F20]/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
      
      {/* Header with Logo */}
      <header className="relative z-10 pt-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#FD5F20] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">ato creative</h1>
              <p className="text-xs text-gray-500 -mt-1">full-service digital agency</p>
            </div>
          </div>
          <div className="hidden md:block">
            <span className="text-sm text-gray-600">Professional Digital Solutions</span>
          </div>
        </div>
      </header>

      <div className="relative flex flex-col items-center justify-center min-h-screen p-6">
        <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#FD5F20]/10 border border-[#FD5F20]/20">
              <span className="text-sm font-medium text-[#FD5F20]">🚀 All-in-One Digital Agency</span>
            </div>

            {/* Headlines */}
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Complete Digital
                <span className="block text-[#FD5F20]">Solutions</span>
              </h1>
              <h2 className="text-xl lg:text-2xl text-gray-600 font-light">
                Professional services to grow your brand online
              </h2>
            </div>

            {/* Results */}
            <div className="flex space-x-8">
              {results.map((result, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-[#FD5F20]">{result.value}</div>
                  <div className="text-xs text-gray-600 mt-1">{result.label}</div>
                </div>
              ))}
            </div>

            {/* Description */}
            <p className="text-lg text-gray-600 leading-relaxed">
              Ato Creative provides comprehensive digital marketing services including social media management, 
              video production, web development, and brand design. We help businesses establish a powerful 
              online presence and drive measurable results.
            </p>

            {/* Services Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 rounded-lg hover:bg-white/50 transition-colors group border border-gray-100">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#FD5F20]/10 rounded-lg flex items-center justify-center group-hover:bg-[#FD5F20]/20 transition-colors">
                    <div className="text-[#FD5F20]">
                      {service.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{service.title}</h3>
                    <p className="text-gray-600 text-xs leading-relaxed">{service.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <div className="mb-4 sm:mb-0">
                <p className="font-semibold text-gray-900">Ready to transform your digital presence?</p>
                <p className="text-gray-600 text-sm">Get professional services tailored to your needs</p>
              </div>
              <button 
                onClick={handleOpenModal}
                className="group flex items-center justify-center rounded-full bg-[#FD5F20] text-white px-6 py-4 hover:bg-[#E5561D] transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <span className="mr-3 font-semibold">Get Started</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="relative">
            <div className="relative bg-white rounded-3xl p-8 border border-gray-200 shadow-2xl">
              {/* Service Dashboard Mockup */}
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#FD5F20] rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold">A</span>
                    </div>
                    <div>
                      <div className="h-3 w-32 bg-gray-800 rounded mb-1"></div>
                      <div className="h-2 w-24 bg-gray-400 rounded"></div>
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-gray-600" />
                  </div>
                </div>

                {/* Service Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="h-3 bg-gray-700 rounded w-16"></div>
                    </div>
                    <div className="h-4 bg-green-100 rounded flex items-center justify-center">
                      <span className="text-xs font-medium text-green-700">Active</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="h-3 bg-gray-700 rounded w-20"></div>
                    </div>
                    <div className="h-4 bg-blue-100 rounded flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-700">In Progress</span>
                    </div>
                  </div>
                </div>

                {/* Projects Overview */}
                <div className="space-y-3">
                  <div className="h-4 bg-gray-800 rounded w-1/3"></div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { name: "Social Media", progress: 75, color: "bg-[#FD5F20]" },
                      { name: "Web Dev", progress: 90, color: "bg-blue-500" },
                      { name: "Video Prod", progress: 60, color: "bg-green-500" }
                    ].map((project, index) => (
                      <div key={index} className="text-center">
                        <div className="h-3 bg-gray-300 rounded mb-2"></div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${project.color} transition-all duration-500`}
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Projects */}
                <div className="space-y-3">
                  <div className="h-4 bg-gray-800 rounded w-1/2"></div>
                  {[
                    { service: "Social Media Campaign", client: "Tech Startup Co", status: "Live" },
                    { service: "Website Redesign", client: "Fashion Brand", status: "Development" },
                    { service: "Video Ad Production", client: "Local Restaurant", status: "Editing" }
                  ].map((project, index) => (
                    <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                          {index === 0 && <Megaphone className="w-4 h-4 text-gray-600" />}
                          {index === 1 && <Code className="w-4 h-4 text-gray-600" />}
                          {index === 2 && <Video className="w-4 h-4 text-gray-600" />}
                        </div>
                        <div>
                          <div className="h-3 bg-gray-400 rounded mb-1 w-24"></div>
                          <div className="h-2 bg-gray-200 rounded w-16"></div>
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        project.status === 'Live' ? 'bg-green-100 text-green-700' :
                        project.status === 'Development' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {project.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#FD5F20]/10 rounded-2xl rotate-12 flex items-center justify-center">
              <Rocket className="w-8 h-8 text-[#FD5F20]/40" />
            </div>
            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-[#FD5F20]/5 rounded-2xl -rotate-12 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#FD5F20]/30" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative pb-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-gray-500">
            © 2024 Ato Creative. All rights reserved. 
            <span className="mx-2">•</span>
            Professional Digital Marketing Services
          </p>
        </div>
      </footer>

      {/* Render LoginModal */}
      <LoginModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
}

export default CommunityTool;